import React, { MouseEventHandler, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Form, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { gql, useMutation } from 'urql';
import { HacpackMutation, HacpackMutationAssignDatesToHandlingsArguments } from '../types/graph';
import { useId } from '../hooks';
import { Handling, Terminal } from '../types/graph';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { startTransition } from 'react';

type TerminalHandlingsAssignDateProps = {
    terminal: Terminal,
    handlings: Handling[],
};

type HacpackMutationAssignDatesToHandlingsResult = Pick<HacpackMutation, 'assignDatesToHandlings'>;

const changeHandlingPositionMutation = gql<HacpackMutationAssignDatesToHandlingsResult, HacpackMutationAssignDatesToHandlingsArguments>`
    mutation ($handlingDates: [HandlingDateInput!]!) {
        assignDatesToHandlings(handlingDates: $handlingDates)  {
            isOk
        }
    }
`;

const TerminalHandlingsAssignDateButton = ({ terminal, handlings }: TerminalHandlingsAssignDateProps) => {

    const id = useId();

    const { control, register, handleSubmit } = useForm<HacpackMutationAssignDatesToHandlingsArguments>({
        values: { handlingDates: [] }
    });

    const { fields, replace, update } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormProvider)
        name: "handlingDates", // unique name for your Field Array
    });

    const [formHandlings, setFormHanglings] = useState<Handling[]>([]);

    //const isAdmin = terminalId === undefined ? false : hasRoleForTerminal(RoleName.ADMIN, terminalId)

    const [result, assignDates] = useMutation<HacpackMutationAssignDatesToHandlingsResult, HacpackMutationAssignDatesToHandlingsArguments>(changeHandlingPositionMutation);
    // @ts-ignore
    const { error, fetchingApproval } = result;

    const [promptOpen, setPromptOpen] = useState(false);

    const [dateForAll, setDateForAll] = useState("");

    const { t } = useTranslation('common');

    const toggleForm = useCallback(() => setPromptOpen(promptOpen => !promptOpen), [setPromptOpen])

    const openForm = useCallback(() => {
        setPromptOpen(true);
    }, [setPromptOpen]);

    const closeForm = useCallback(() => {
        setPromptOpen(false);
    }, [setPromptOpen]);

    //const handleDeleteButtonClick = useCallback(() => {
    //    if (handlingId === undefined) {
    //        return;
    //    }

    //    const args: HacpackMutationDeleteHandlingsArguments = {
    //        handlingIds: [handlingId]
    //    };

    //    deleteHandlingsHarbourOfficeApprovals(args).then(({ data }) => {
    //        console.log({data})

    //        if (data?.deleteHandlings?.isOk !== true) {
    //            return;
    //        }
    //        window.location.reload();
    //    });
    //}, [handlingId]);

    const isButtonDisabled = handlings?.length === 0 ?? false;

    //if (dangerousGoodsCount > 0 && (handlingId === undefined || handlingCheckedIn === undefined || (!(isSuperuser || isQsa) && handlingCheckedIn !== false))) {
    //    return null;
    //}

    const onSubmit: SubmitHandler<HacpackMutationAssignDatesToHandlingsArguments> = useCallback(async ({ handlingDates }) => {
        //console.log(data)
        const result = await assignDates({
            handlingDates: handlingDates.map(({ handlingId, checkInDate }) => ({
                handlingId,
                checkInDate: `${checkInDate}T00:00:00`
            }))
        });

        if (result.error) {
            window.alert("Si è verificato un errore nel server.")
            return;
        }

        const isOk = result?.data?.assignDatesToHandlings?.isOk ?? false;

        if (isOk === false) {
            window.alert("Non è stato possibile assegnare le date.")
            return;
        }

        window.location.reload();
    }, [assignDates]);

    const resetField = useCallback(() => replace(handlings.map(h => ({ handlingId: h.id!, checkInDate: h.assignedDate?.substr(0, 10) ?? "" }))), [replace, handlings])

    const handleAssignButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback((ev) => {
        startTransition(() => {
            setFormHanglings(handlings);
            resetField();
            openForm();
        })
    }, [handlings, setFormHanglings, resetField, openForm])

    const assignDateToAll = useCallback((checkInDate: string) => replace(handlings.map(h => ({ handlingId: h.id!, checkInDate }))), [handlings, replace])

    const formValues = useMemo(() => formHandlings.map((h, i) => {

        const requestedDate = (h.requestedDate?.trim()?.length ?? 0) > 0 ? h.requestedDate! : null;

        return {
            unitNumber: h.loadingUnitIdentifier,
            handlingId: h.id!,
            booking: h.booking!,
            requestedDate: requestedDate && {
                value: requestedDate.substr(0, 10),
                str: new Date(Date.parse(requestedDate)).toLocaleDateString()
            },
            field: fields[i]
        }
    }), [fields, formHandlings]);

    const frm = <div>
        <Modal isOpen={promptOpen} toggle={toggleForm}>
            <ModalHeader>
                <h3>{t('Assegna data')}</h3>
            </ModalHeader>
            <ModalBody>
                <div className="input-group mb-5 flex">
                    <input value={dateForAll} onChange={(ev) => setDateForAll(ev.target.value)} type="date" className="form-control" />
                    <button onClick={() => assignDateToAll(dateForAll)} disabled={fetchingApproval || dateForAll.length !== 10} className="btn btn-outline-secondary" type="button">Riempi tutti</button>
                </div>

                <Form id={`form${id}`} onSubmit={handleSubmit(onSubmit)} disabled={fetchingApproval}>
                    {formValues.map(({ unitNumber, booking, handlingId, requestedDate, field }, index) => (
                        <div className="pb-4" key={field.id}>
                            <Label htmlFor={`${id}handlingDates${index}checkInDate`}>Autorizzazione <b>{unitNumber}</b><br />Booking <b>{booking}</b></Label>
                            <input type="hidden" {...register(`handlingDates.${index}.handlingId`)} />
                            <input id={`${id}handlingDates${index}checkInDate`} className="form-control" type="date" {...register(`handlingDates.${index}.checkInDate`, { required: true })} />
                            {requestedDate && <p className="form-text">
                                Data richiesta:{' '}
                                <a title={t('Assegna').toString()} href="#" onClick={(ev) => { ev.preventDefault(); update(index, { handlingId, checkInDate: requestedDate.value }) }}>{requestedDate.str.toString()}</a>
                            </p>}
                        </div>
                    ))}
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button disabled={fetchingApproval} type="submit" form={`form${id}`} color="primary">{t('Conferma')}</Button>{' '}
                <Button disabled={fetchingApproval} color="secondary" onClick={toggleForm}>{t('Annulla')}</Button>
            </ModalFooter>
        </Modal>
    </div>


    return <>
        <Button color='primary' disabled={isButtonDisabled} type='button' onClick={handleAssignButtonClick}>
            {t('Assegna Data')}{' '}
            {handlings.length > 0 && <Badge color="text">{handlings.length}</Badge>}
        </Button>
        {frm}
    </>
    //<p><b>{t('Approvazione Capitaneria')}:</b> {authorization} <Button color='danger' disabled={fetching} type='button' onClick={handleDeleteButtonClick}>{t('Elimina')}</Button></p>
};

export default TerminalHandlingsAssignDateButton;